import React, { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List } from 'immutable';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { appendCounter, deleteCounter, refresh, startFind, endFind } from '../actions/autoComplete';
import { getOriginal, unwrap } from '../utils/ChangeSpy';
import BaseAutoComplete from '../components/helpers/AutoComplete';
import PrettyError from '../components/decorators/PrettyError';
import DisplayError from '../components/decorators/DisplayError';
import uniqueId from '../utils/uniqueId';

const AutoComplete = PrettyError(DisplayError(BaseAutoComplete));

const AutoCompleteContainer = ({
  appendCounter,
  deleteCounter,
  refresh,
  startFind,
  endFind,
  onFind,
  valueName,
  idName = '',
  suggestionsName = '',
  onChange,
  inputProps,
  suggestions = List(),
  value = '',
  idValue,
  render,
  focusInputOnSuggestionClick,
  suggestIfEmpty,
  loading,
  loadable = true,
  highlightFirst,
  addNew,
  appendToContainer = false,
  minSuggestLength,
  autoComplete,
  onSelectCallback,
  rollbackOnBlur = true,
}) => {
  const id = useMemo(() => uniqueId(), []);

  useEffect(() => {
    appendCounter(id);

    return () => {
      deleteCounter(id);
    };
  }, [id, appendCounter, deleteCounter]);

  const getCounter = () => autoComplete.get(id);

  const handleFetchData = ({ value }) => {
    refresh(id);
    startFind(id);
    onFind({
      filter: value,
      page: 1,
      afterSuccess: ({ response }) => endFind(id, response),
    });
  };

  const handleFetchNextData = ({ value }) => {
    const counter = getCounter();

    if (counter.get('hasOther')) {
      startFind(id);
      onFind({
        filter: value,
        page: counter.get('page'),
        afterSuccess: ({ response }) => endFind(id, response),
      });
    }
  };

  const handleUpdateSuggestions = param => {
    if ('text' in param) {
      onChange({ name: valueName, value: param.text });
    }

    if ('suggestions' in param) {
      onChange({ name: suggestionsName, value: param.suggestions });
    }
  };

  const handleSelect = (event, { suggestion }) => {
    if (suggestion) {
      onChange({ name: valueName, value: suggestion.text });
      onChange({ name: idName, value: suggestion.id });
    }

    if (onSelectCallback) {
      onSelectCallback(suggestion);
    }
  };

  const handleBlur = () => {
    refresh(id);

    if (rollbackOnBlur) {
      const originalValue = getOriginal(value);
      const originalIdValue = getOriginal(unwrap(idValue));

      if (idValue !== null && !idValue) {
        onChange({ name: valueName, originalValue });
        onChange({ name: idName, value: originalIdValue });
      }
    }
  };

  const getLoading = () => {
    if (!loadable) {
      return false;
    }

    if (loading) {
      return loading;
    }

    const counter = getCounter();

    return counter ? counter.get('loading') : false;
  };

  const renderSuggestion = suggestion => {
    const { text, ...rest } = suggestion;

    return render ? render(suggestion) : <span {...rest}>{text}</span>;
  };

  const name = inputProps?.name || valueName;

  const getSuggestionValue = suggestion => suggestion?.value ?? '';

  return (
    <AutoComplete
      addNew={addNew}
      appendToContainer={appendToContainer}
      checkOn="text"
      focusInputOnSuggestionClick={focusInputOnSuggestionClick}
      formGroupClass=" "
      getNextSuggestion={handleFetchNextData}
      getSuggestion={handleFetchData}
      getSuggestionValue={getSuggestionValue}
      highlightFirst={highlightFirst}
      inputProps={{ ...inputProps, onBlur: handleBlur }}
      loading={getLoading()}
      minSuggestLength={minSuggestLength}
      name={name}
      onSuggestionSelected={handleSelect}
      onUpdateSuggestions={handleUpdateSuggestions}
      renderSuggestion={renderSuggestion}
      suggestIfEmpty={suggestIfEmpty}
      suggestions={suggestions}
      text={value}
    />
  );
};

AutoCompleteContainer.propTypes = {
  appendCounter: PropTypes.func.isRequired,
  deleteCounter: PropTypes.func.isRequired,
  endFind: PropTypes.func.isRequired,
  idName: PropTypes.string.isRequired,
  inputProps: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onFind: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  render: PropTypes.func,
  rootPath: PropTypes.arrayOf(PropTypes.string),
  startFind: PropTypes.func.isRequired,
  suggestIfEmpty: PropTypes.bool,
  suggestions: PropTypes.instanceOf(List),
  suggestionsName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  valueName: PropTypes.string.isRequired,
};

const mapStateToProps = (state, props) => {
  const getField = (field, name) => {
    if (!props.rootPath) return props[field];

    return props.rootPath.reduce((root, key) => root[key] || {}, state).getIn(name.split('.'));
  };

  return {
    autoComplete: state.autoComplete,
    inputProps: props.inputProps,
    suggestions: getField('suggestions', props.suggestionsName),
    value: getField('value', props.valueName) || '',
    idValue: getField('idValue', props.idName),
  };
};

export default memo(
  connect(
    mapStateToProps,
    {
      appendCounter,
      deleteCounter,
      startFind,
      endFind,
      refresh,
    },
    mergeProps,
    connectOptions,
  )(AutoCompleteContainer),
);
